import React from 'react';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import styles from './PricingSummary.module.scss';
import { useI18n } from 'next-localization';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import { useFetchDailyPricingQuery } from '@/store/fundCentre/services/fundCentreAPI';
import { DailyPricingRequest } from 'model/ApiGateway';
import { useRouter } from 'next/router';
import { ComponentProps } from 'lib/component-props';
import useHelpers from 'hooks/helpers';
import { dateTimeFormatOptions } from 'constants/dateFormats';

const ALL_FIELDS = [
  { name: 'sharePrice', type: 'currency' },
  { name: 'estimatedNavCumIncome', type: 'currency' },
  { name: 'estimatedNavExIncome', type: 'currency' },
  { name: 'lastActualCumIncomeNav', type: 'currency' },
  { name: 'dividendYield', type: 'percent' },
  { name: 'discountPremiumToNavCumIncome', type: 'percent' },
  { name: 'discountPremiumToNavExIncome', type: 'percent' },
] as const;

const getFieldsToDisplay = (siteName?: string, pageName?: string) => {
  if (siteName === 'Diversified') return ALL_FIELDS.filter((f) => f.name !== 'dividendYield');
  if (siteName === 'PIT' && pageName === 'apit') {
    return ALL_FIELDS.filter(
      (f) => f.name !== 'lastActualCumIncomeNav' && f.name !== 'dividendYield'
    );
  }
  return ALL_FIELDS;
};

const getTypeSymbol = (type: string): string => {
  switch (type) {
    case 'currency':
      return 'p';
    case 'percent':
      return '%';
    default:
      return '';
  }
};

const PricingSummary: React.FC<ComponentProps> = ({ rendering }) => {
  const { t } = useI18n();
  const {
    query: { path },
  } = useRouter();
  const {
    sitecoreContext: { language, site, route },
  } = useSitecoreContext();
  const { localizeDate, formatNumber } = useHelpers();
  const routeFields = route.fields ?? {};

  const apiData: DailyPricingRequest = {
    language: language || 'en-GB',
    currency: 'GBP',
    securityCode: routeFields?.trustId?.value ?? null,
    securityType: Number(routeFields?.trustType?.value ?? 0),
  };
  const { data } = useFetchDailyPricingQuery(apiData);
  const results = data;

  const bottomLineExist = !!results?.currency && !!results?.dateTime;

  const formattedDate = results?.dateTime
    ? localizeDate(results.dateTime, dateTimeFormatOptions)
    : '';
  const displayedFields = getFieldsToDisplay(site?.name, path?.[0]);

  const getValue = (item: any, type: string): string => {
    return `${formatNumber(item)} ${getTypeSymbol(type)}`;
  };
  return (
    <section
      id={rendering.uid}
      data-e2e-component-uid={rendering.uid}
      className="container background-white pt-4 pb-5"
    >
      <div className="col-12 col-md-8">
        <Text tag="h2" field={{ value: t('fundsdailyPricing') }} className="background-to-change" />
      </div>

      {results && (
        <div className="row d-flex">
          {displayedFields.map(
            (field) =>
              results[field.name] !== undefined && (
                <div key={field.name} className={`styles.label col-12 col-md-4`}>
                  <div className={`body-default text-grey pt-2 ${styles.pricingsTitle}`}>
                    <span>{t(`funds${field.name}`)}</span>
                  </div>
                  <p className="h3 pt-1 pb-2 d-flex align-items-center">
                    <span>{getValue(results[field.name], field.type)}</span>
                  </p>
                </div>
              )
          )}
        </div>
      )}

      {bottomLineExist && (
        <div className={`${styles.bottomLine} text-grey pt-1`}>
          <span>
            {results.currency} | {t('fundslondonStockExchange')} | {t('asOf')} {formattedDate} | D
          </span>
        </div>
      )}
    </section>
  );
};

export default PricingSummary;
